import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import App from './App';
import reportWebVitals from './reportWebVitals';
import './pwa';

const GOOGLE_CONFIG = {
  development: {
    clientId:
      '252207045991-fnki4viicsusg92ad880j4d5j7smf1fk.apps.googleusercontent.com'
  },
  production: {
    clientId:
      '384237876591-ddf5ftv0bqntdbl1d8e3u4tpgq60927u.apps.googleusercontent.com'
  }
};

export const getGoogleClientId = () => {
  return import.meta.env.MODE === 'production'
    ? GOOGLE_CONFIG.production.clientId
    : GOOGLE_CONFIG.development.clientId;
};

// --- Stale chunk recovery ---
// Each deploy ships only its own /assets, so a tab still running the previous
// build fails to load a lazy chunk the moment it navigates to a route it
// hasn't visited yet ("Failed to fetch dynamically imported module", Safari's
// "'text/html' is not a valid JavaScript MIME type", "Unable to preload CSS").
// Reloading picks up the current index.html and its chunk names — but the SW
// serves navigations from its precached index.html, so the new SW has to be
// installed first or the reload just brings the old build back. The
// timestamp stops a reload loop when a chunk is genuinely broken — then the
// error is left to throw and reach Sentry.
const PRELOAD_RELOAD_KEY = 'vite-preload-reload-at';
let preloadReloading = false;

const waitForNewServiceWorker = async () => {
  const registration = await navigator.serviceWorker?.getRegistration();
  if (!registration) return;
  await registration.update();
  const incoming = registration.installing || registration.waiting;
  if (!incoming) return; // sw.js unchanged — nothing to wait for
  // sw.js calls skipWaiting() on install, so this normally takes a moment.
  await new Promise<void>((resolve) => {
    const settle = () => {
      if (incoming.state === 'activated' || incoming.state === 'redundant') {
        resolve();
      }
    };
    incoming.addEventListener('statechange', settle);
    window.setTimeout(resolve, 8000);
    settle();
  });
};

window.addEventListener('vite:preloadError', (event) => {
  if (preloadReloading) {
    event.preventDefault();
    return;
  }
  let lastReload = 0;
  try {
    lastReload = Number(sessionStorage.getItem(PRELOAD_RELOAD_KEY)) || 0;
  } catch {
    // storage unavailable — fall through and reload once
  }
  if (Date.now() - lastReload < 10_000) return;
  preloadReloading = true;
  event.preventDefault();
  waitForNewServiceWorker()
    .catch(() => {
      // update check failed (offline, no SW) — reload anyway
    })
    .finally(() => {
      try {
        sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()));
      } catch {
        // ignore
      }
      window.location.reload();
    });
});

// --- Preboot removal (remove the HTML splash once JS is running) ---
// const preboot = document.getElementById('preboot');
// if (preboot) {
//   preboot.classList.add('preboot--hide');
//   requestAnimationFrame(() => {
//     window.setTimeout(() => preboot.remove(), 320);
//   });
// }

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  // <React.StrictMode>
  <HelmetProvider>
    {/*
      GoogleOAuthProvider injects the GSI script and exposes
      `scriptLoadedSuccessfully` via context. It never throws and never
      blocks rendering — if GSI can't load (offline / blocked network) it
      simply reports failure through `onScriptLoadError`, and consumers
      (see containers/Login/google.tsx) hide the Google button. The app
      boots and works either way.
    */}
    <GoogleOAuthProvider
      clientId={getGoogleClientId()}
      onScriptLoadError={() => {
        // Non-fatal: Google sign-in is disabled for this session, nothing else.
        // eslint-disable-next-line no-console
        console.warn(
          '[auth] Google Identity Services failed to load; Google sign-in disabled.'
        );
      }}>
      <App />
    </GoogleOAuthProvider>
  </HelmetProvider>
  // </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
